import { useState } from "react";
import { Anchor, Badge, Button, Card, Collapse, Grid, Group, SegmentedControl, Stack, Text, Textarea, Title, Tooltip } from "@mantine/core";
import { IconBasket, IconCircleCheck, IconSearch, IconThumbDown, IconThumbUp, IconWorld } from "@tabler/icons-react";
import { groceryReplacementsFromExecution, type GroceryItem, type GroceryNutrients, type GroceryProduct, type GroceryReplacementResult } from "@passio/easyai-client";
import { easyai } from "../lib/clients";
import { PageHeader, StatCard } from "../theme/components";
import { RequireFeature } from "../components/common";
import { JsonView } from "../components/JsonView";
import { FoodDetailDrawer } from "../components/food";
import { ChainError, RunProgressCard, useChainRun, type RunStage } from "../components/ChainRun";

const CHAIN_KEY = "grocery_swaps";
const STAGES: RunStage[] = [
  { label: "Reading your grocery list", throughNode: 1 },
  { label: "Searching the food database", throughNode: 2 },
  { label: "Filtering to relevant options", throughNode: 3 },
  { label: "Narrowing to the best candidates", throughNode: 4 },
  { label: "Ranking the best replacements", throughNode: 5 },
  { label: "Preparing your results", throughNode: 6 },
];
const EXAMPLES: { label: string; text: string }[] = [
  { label: "Weekly shop", text: "Honey Nut Cheerios\nWonder white bread\nSkippy peanut butter\nCoca-Cola\nTropicana orange juice\nRanch dressing" },
  { label: "Snacks", text: "Doritos Nacho Cheese\nClif Bar chocolate chip\nGoldfish crackers\nM&M's peanut\nYoplait strawberry yogurt" },
  { label: "Pantry", text: "Spaghetti pasta\nRagu tomato sauce\nCanola oil\nKraft mac and cheese\nQuaker instant oatmeal" },
];

const scoreTone = (v?: number) => (typeof v !== "number" ? "gray" : v >= 0.7 ? "green" : v >= 0.4 ? "amber" : "red");
const pct = (v?: number) => (typeof v === "number" ? `${Math.round(v * 100)}` : "–");
const fmtNum = (v: number) => (Math.abs(v) >= 10 || Number.isInteger(v) ? `${Math.round(v)}` : `${Math.round(v * 10) / 10}`);

function nutOf(n: GroceryNutrients | undefined, keys: string[]): number | undefined {
  if (!n) return undefined;
  const lower: Record<string, number> = {};
  for (const [k, v] of Object.entries(n)) if (typeof v === "number" && !Number.isNaN(v)) lower[k.toLowerCase()] = v;
  for (const k of keys) if (typeof lower[k] === "number") return lower[k];
  return undefined;
}

function ScorePill({ label, value, hint }: { label: string; value?: number; hint: string }) {
  return (
    <Tooltip label={hint} withArrow multiline w={240}>
      <span className={`pd-score ${scoreTone(value)}`}>
        {label} {pct(value)}
      </span>
    </Tooltip>
  );
}

function Stat({ label, value, unit = "" }: { label: string; value?: number; unit?: string }) {
  if (typeof value !== "number") return null;
  return (
    <div className="pd-stat">
      <b>
        {fmtNum(value)}
        {unit}
      </b>
      <span>{label}</span>
    </div>
  );
}

function ProductCard({ p, kind, onOpen }: { p: GroceryProduct; kind: "original" | "best" | "alt"; onOpen?: (ref: string, name: string) => void }) {
  const [showIngredients, setShowIngredients] = useState(false);
  const n = p.nutrients;
  const calories = nutOf(n, ["calories_kcal", "energy_kcal", "calories", "kcal", "energy"]);
  const protein = nutOf(n, ["protein_g", "protein"]);
  const fat = nutOf(n, ["fat_g", "total_fat_g", "totalfat_g", "fat"]);
  const carbs = nutOf(n, ["carbs_g", "carbohydrates_g", "total_carbs_g", "carbohydrate_g", "carbs"]);
  const netCarbs = typeof p.net_carbs_g === "number" ? p.net_carbs_g : nutOf(n, ["net_carbs_g"]);
  const notes = p.notes ?? [];
  return (
    <div className={`pd-product${kind === "original" ? " original" : kind === "best" ? " best" : ""}`}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <div style={{ minWidth: 0 }}>
          {kind === "best" && (
            <Badge size="xs" color="green" variant="filled" mb={4}>
              Best pick
            </Badge>
          )}
          <Text fw={700} lh={1.3}>
            {p.name || "Unnamed product"}
          </Text>
          {(p.brand || p.serving) && (
            <Text fz="xs" c="dimmed">
              {[p.brand, p.serving].filter(Boolean).join(" · ")}
            </Text>
          )}
        </div>
        {kind !== "original" && typeof p.ai_score === "number" && (
          <Tooltip label="How well the AI ranker thinks this replaces the original (0–100)" withArrow>
            <span className={`pd-score ${scoreTone(p.ai_score)}`} style={{ fontSize: 13 }}>
              AI {pct(p.ai_score)}
            </span>
          </Tooltip>
        )}
      </Group>
      <Group gap={6} mt="xs">
        <ScorePill label="Keto" value={p.keto_score} hint="Keto friendliness (0–100, higher is better)" />
        <ScorePill label="Whole food" value={p.upf_score} hint="Processing level (0–100, higher means less ultra-processed)" />
      </Group>
      {[calories, protein, fat, carbs, netCarbs].some((v) => typeof v === "number") && (
        <Group gap={6} mt="sm">
          <Stat label="Cal" value={calories} />
          <Stat label="Net carb" value={netCarbs} unit="g" />
          <Stat label="Carb" value={carbs} unit="g" />
          <Stat label="Protein" value={protein} unit="g" />
          <Stat label="Fat" value={fat} unit="g" />
        </Group>
      )}
      {p.ai_reason && (
        <Text fz="sm" mt="sm">
          {p.ai_reason}
        </Text>
      )}
      {notes.length > 0 && (
        <Group gap={4} mt="xs">
          {notes.slice(0, 4).map((x) => (
            <Badge key={x} size="xs" variant="light" color={kind === "original" ? "orange" : "gray"} tt="none">
              {x}
            </Badge>
          ))}
        </Group>
      )}
      <Group gap="sm" mt="xs">
        {p.ingredients && (
          <Anchor component="button" type="button" fz="xs" onClick={() => setShowIngredients((s) => !s)}>
            {showIngredients ? "Hide ingredients" : "Ingredients"}
          </Anchor>
        )}
        {p.ref_code && onOpen && (
          <Anchor component="button" type="button" fz="xs" onClick={() => onOpen(p.ref_code!, p.name ?? "")}>
            Full nutrition
          </Anchor>
        )}
      </Group>
      <Collapse in={showIngredients}>
        <Text fz="xs" c="dimmed" mt={4}>
          {p.ingredients}
        </Text>
      </Collapse>
    </div>
  );
}

function ItemFeedback({ executionId, item }: { executionId: string; item: string }) {
  const [sent, setSent] = useState<string | null>(null);
  const send = (label: "thumbs_up" | "thumbs_down") => {
    void easyai.sendFeedback([{ source_type: "chain_execution", source_id: executionId, label, item_ref: item.slice(0, 200), metadata: { source: "passio-demo" } }]).catch(() => {});
    setSent(label);
  };
  if (sent) return <Text fz="xs" c="dimmed">Thanks!</Text>;
  return (
    <Group gap={4}>
      <Tooltip label="Good suggestions">
        <Button size="compact-xs" variant="subtle" color="gray" onClick={() => send("thumbs_up")} aria-label="Good suggestions">
          <IconThumbUp size={14} />
        </Button>
      </Tooltip>
      <Tooltip label="Not helpful">
        <Button size="compact-xs" variant="subtle" color="gray" onClick={() => send("thumbs_down")} aria-label="Not helpful">
          <IconThumbDown size={14} />
        </Button>
      </Tooltip>
    </Group>
  );
}

function ItemRow({ item, executionId, onOpen }: { item: GroceryItem; executionId: string; onOpen: (ref: string, name: string) => void }) {
  const [showAll, setShowAll] = useState(false);
  const reps = item.suggested_replacements;
  const shown = showAll ? reps : reps.slice(0, 3);
  const original: GroceryProduct = item.original_item_details ?? { name: item.original_item };
  return (
    <Card>
      <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
        <Group gap="xs">
          <Title order={4} fz={17}>
            {item.original_item}
          </Title>
          {item.original_already_good && (
            <Badge color="green" variant="light" leftSection={<IconCircleCheck size={12} />}>
              Already a good choice
            </Badge>
          )}
          {item.used_web_fallback && (
            <Tooltip label="Nothing suitable in the catalog, so the chain suggested ideas from the web">
              <Badge color="blue" variant="light" leftSection={<IconWorld size={12} />}>
                Web ideas
              </Badge>
            </Tooltip>
          )}
          {item.had_internal_matches === false && !item.used_web_fallback && (
            <Badge color="gray" variant="light">
              No catalog matches
            </Badge>
          )}
        </Group>
        <ItemFeedback executionId={executionId} item={item.original_item} />
      </Group>
      <div className="pd-swap-grid">
        <div>
          <Text className="pd-eyebrow" mb={6}>
            You picked
          </Text>
          <ProductCard p={original} kind="original" onOpen={onOpen} />
        </div>
        <div>
          <Text className="pd-eyebrow" mb={6}>
            {reps.length ? `Swap for (${reps.length})` : "Swaps"}
          </Text>
          {reps.length === 0 ? (
            <div className="pd-product">
              <Text fz="sm" c="dimmed">
                {item.original_already_good ? "This one already fits the rules, so no swap is needed." : "No replacement met the keto and processing rules for this item."}
              </Text>
            </div>
          ) : (
            <Stack gap="sm">
              {shown.map((p, i) => (
                <ProductCard key={`${p.ref_code ?? p.name}-${i}`} p={p} kind={i === 0 ? "best" : "alt"} onOpen={onOpen} />
              ))}
              {reps.length > 3 && (
                <Anchor component="button" type="button" fz="sm" onClick={() => setShowAll((s) => !s)}>
                  {showAll ? "Show fewer" : `Show ${reps.length - 3} more`}
                </Anchor>
              )}
            </Stack>
          )}
        </div>
      </div>
    </Card>
  );
}

type Filter = "all" | "swaps" | "good";

export function GrocerySwapsPage() {
  const [text, setText] = useState("");
  const [result, setResult] = useState<{ data: GroceryReplacementResult | null; executionId: string; raw: unknown; ms: number } | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [showRaw, setShowRaw] = useState(false);
  const [food, setFood] = useState<{ ref: string; name: string } | null>(null);
  const r = useChainRun();
  const lines = text.split(/\n|,/).map((s) => s.trim()).filter(Boolean);

  const submit = async () => {
    if (!lines.length || r.running) return;
    const t0 = performance.now();
    const exe = await r.run(CHAIN_KEY, { grocery_input: lines.join("\n") });
    if (!exe) return;
    setResult({ data: groceryReplacementsFromExecution(exe), executionId: exe.id, raw: exe, ms: performance.now() - t0 });
    setFilter("all");
  };

  const data = result?.data;
  const items = (data?.items ?? []).filter((it) => (filter === "swaps" ? it.suggested_replacements.length > 0 : filter === "good" ? it.original_already_good : true));
  const total = data?.summary?.total_items ?? data?.items.length ?? 0;
  const withSwaps = data?.summary?.items_with_replacements ?? data?.items.filter((i) => i.suggested_replacements.length).length ?? 0;
  const good = data?.summary?.items_already_good ?? data?.items.filter((i) => i.original_already_good).length ?? 0;

  return (
    <RequireFeature feature="grocery_swaps">
      <PageHeader
        eyebrow="EasyAI chain · keto and low-UPF grocery replacement"
        title="Keto grocery swaps"
        description="Paste a grocery list. The chain finds each item in the Passio catalog, then ranks keto-friendly, less processed replacements for each one."
      />
      <Grid gutter="lg" align="flex-start">
        <Grid.Col span={{ base: 12, lg: 4 }}>
        <Card>
          <Stack gap="sm">
            <Textarea
              label="Grocery list"
              description="One item per line (or comma separated). Brand names help."
              placeholder={"Honey Nut Cheerios\nWonder white bread\nCoca-Cola"}
              autosize
              minRows={8}
              maxRows={18}
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submit();
                }
              }}
              disabled={r.running}
            />
            <Group gap={6}>
              <Text fz="xs" c="dimmed">
                Try:
              </Text>
              {EXAMPLES.map((x) => (
                <Badge key={x.label} variant="outline" color="gray" style={{ cursor: "pointer" }} onClick={() => setText(x.text)}>
                  {x.label}
                </Badge>
              ))}
            </Group>
            <Group justify="space-between">
              <Text fz="sm" c="dimmed">
                {lines.length ? `${lines.length} item${lines.length === 1 ? "" : "s"}` : "Add a few items"}
              </Text>
              <Button leftSection={<IconSearch size={16} />} onClick={() => void submit()} disabled={!lines.length} loading={r.running}>
                Find swaps
              </Button>
            </Group>
            <Text fz="xs" c="dimmed">
              Larger lists take longer: every item is searched and ranked separately.
            </Text>
          </Stack>
        </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 8 }}>
        <Stack gap="md">
          {r.running && <RunProgressCard title="Finding better swaps" progress={r.progress} stages={STAGES} onCancel={r.cancel} />}
          <ChainError error={r.error} onRetry={() => void submit()} />
          {!result && !r.running && !r.error && (
            <Card ta="center" py={40}>
              <Stack align="center" gap="xs">
                <IconBasket size={44} color="var(--pd-green)" />
                <Title order={3} fz={20}>
                  Swaps will appear here
                </Title>
                <Text c="dimmed" maw={420}>
                  Each item is shown next to ranked replacements with keto and processing scores, net carbs and the reasons behind the pick.
                </Text>
              </Stack>
            </Card>
          )}
          {result && !r.running && (
            <>
              {data ? (
                <>
                  <StatCard
                    caption="Results"
                    title={`${withSwaps} of ${total} item${total === 1 ? "" : "s"} have better swaps`}
                    stats={[
                      { value: String(total), label: "items" },
                      { value: String(withSwaps), label: "with swaps" },
                      { value: String(good), label: "already good" },
                      { value: `${(result.ms / 1000).toFixed(0)}s`, label: "run time" },
                    ]}
                    accent
                  />
                  <SegmentedControl
                    value={filter}
                    onChange={(v) => setFilter(v as Filter)}
                    data={[
                      { value: "all", label: `All (${data.items.length})` },
                      { value: "swaps", label: `With swaps (${data.items.filter((i) => i.suggested_replacements.length).length})` },
                      { value: "good", label: `Already good (${data.items.filter((i) => i.original_already_good).length})` },
                    ]}
                  />
                  {items.map((it, i) => (
                    <ItemRow key={`${it.original_item}-${i}`} item={it} executionId={result.executionId} onOpen={(ref, name) => setFood({ ref, name })} />
                  ))}
                  {items.length === 0 && (
                    <Text c="dimmed" ta="center">
                      Nothing in this view.
                    </Text>
                  )}
                </>
              ) : (
                <Card>
                  <Text fw={650}>The chain finished but returned no swap list.</Text>
                  <Text fz="sm" c="dimmed">
                    The raw execution is below.
                  </Text>
                </Card>
              )}
              <Anchor component="button" type="button" fz="sm" onClick={() => setShowRaw((s) => !s)}>
                {showRaw ? "Hide raw execution" : "Show raw execution"}
              </Anchor>
              <Collapse in={showRaw || !data}>
                <JsonView value={result.raw} />
              </Collapse>
            </>
          )}
        </Stack>
        </Grid.Col>
      </Grid>
      <FoodDetailDrawer refCode={food?.ref} name={food?.name} onClose={() => setFood(null)} />
    </RequireFeature>
  );
}
